import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, FormControlLabel, Grid, Stack, Switch, TextField, Typography } from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import MainTitle from "../../Components/Common/MainTitle/MainTitle";
import { useAuthContext } from "../../context/AuthContext";
import { useConsoleHelpLinks } from "../../context/ConsoleHelpLinksContext";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import { isSuperAdmin } from "../../utility/Utils";
import { CONSOLE_HELP_PAGES, createConsoleHelpDraft, hasInvalidConsoleHelpDraft, normalizeConsoleHelpUrl } from "../../utility/consoleHelpLinks";
import { UPDATE_CONSOLE_HELP_LINKS } from "../../graphql/consoleHelpGraphql";

const emptyState = (scope = null) => ({ scope, draft: null, revision: 0, original: "", busy: false, conflict: false, error: null, saved: false });

export default function SuperAdminHelpLinks() {
  const auth = useAuthContext();
  const help = useConsoleHelpLinks();
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const scope = help?.scope;
  const canEdit = Boolean(isSuperAdmin(auth.authState?.user) && !auth.authState?.impersonate?._id && scope);
  const current = useRef({ scope, canEdit });
  current.current = { scope, canEdit };
  const mounted = useRef(true);
  const operation = useRef(0);
  const [state, setState] = useState(() => emptyState());
  const visible = state.scope === scope ? state : { draft: null, busy: false };

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; operation.current += 1; };
  }, []);

  useEffect(() => {
    operation.current += 1;
    setState(previous => canEdit && previous.scope === scope ? previous : emptyState(scope));
  }, [scope, canEdit]);

  useEffect(() => {
    if (!canEdit || !help?.configuration) return;
    setState(previous => {
      // Refresh clean editors while preserving unsaved work and active operations.
      const dirty = previous.draft && JSON.stringify(previous.draft) !== previous.original;
      if (previous.scope === scope && previous.draft && (dirty || previous.conflict || previous.busy)) return previous;
      const draft = createConsoleHelpDraft(help.configuration);
      return { scope, draft, revision: help.configuration.revision, original: JSON.stringify(draft), busy: false, conflict: false, error: null, saved: false };
    });
  }, [canEdit, scope, help?.configuration]);

  const run = async (save) => {
    if (!canEdit || visible.busy || (save && (!visible.draft || visible.conflict || hasInvalidConsoleHelpDraft(visible.draft)))) return;
    const requestScope = scope;
    const sequence = ++operation.current;
    const active = () => mounted.current && current.current.canEdit && current.current.scope === requestScope && operation.current === sequence;
    setState(previous => ({ ...(previous.scope === scope ? previous : emptyState(scope)), busy: true, error: null, saved: false }));
    try {
      let configuration;
      if (save) {
        const response = await axiosGraphQL({
          query: UPDATE_CONSOLE_HELP_LINKS,
          variables: { expectedRevision: visible.revision, links: visible.draft.map(row => ({
            ...row, elUrl: row.elUrl.trim() || null, enUrl: row.enUrl.trim() || null,
          })) },
        }, { withoutClub: true, expectedFirebaseUid: auth.firebaseUser?.uid });
        configuration = response?.data?.data?.updateConsoleHelpLinks;
        if (!Number.isSafeInteger(configuration?.revision) || !Array.isArray(configuration.links)) throw new Error("invalid_help_configuration");
        if (!active()) return;
        // Refresh the shared header links immediately after saving.
        help.refresh().catch(() => {});
      } else {
        configuration = await help.refresh();
      }
      if (!active()) return;
      if (!configuration) {
        setState(previous => ({ ...previous, busy: false }));
        return;
      }
      const draft = createConsoleHelpDraft(configuration);
      setState({ scope, draft, revision: configuration.revision, original: JSON.stringify(draft), busy: false, conflict: false, error: null, saved: save });
    } catch (failure) {
      if (!active()) return;
      const conflict = failure?.codes?.includes("CONSOLE_HELP_LINKS_CONFLICT");
      const forbidden = failure?.codes?.includes("CONSOLE_HELP_LINKS_FORBIDDEN");
      setState(previous => ({ ...previous, busy: false, conflict: Boolean(conflict), error: conflict ? "page_help_conflict" : forbidden ? "page_help_forbidden" : save ? "page_help_save_error" : "page_help_load_error" }));
    }
  };

  if (!canEdit) return <Box component="main" sx={{ p: 3 }}><Alert severity="warning">{t("page_help_forbidden")}</Alert></Box>;
  const update = (pageKey, field, value) => setState(previous => ({
    ...previous, saved: false, draft: previous.draft.map(row => row.pageKey === pageKey ? { ...row, [field]: value } : row),
  }));
  const dirty = Boolean(visible.draft && JSON.stringify(visible.draft) !== visible.original);
  const error = visible.error || (!visible.draft && help?.error ? "page_help_load_error" : null);

  return <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, maxWidth: 1120 }}>
    <Stack spacing={2}>
      <MainTitle title={t("page_help_settings_title")} />
      <Typography color="text.secondary">{t("page_help_settings_description")}</Typography>
      <Alert severity="info">{t("page_help_language_note")}</Alert>
      {error && <Alert severity={visible.conflict ? "warning" : "error"}>{t(error)}</Alert>}
      {visible.saved && <Alert severity="success">{t("page_help_saved")}</Alert>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button type="button" variant="contained" disabled={!dirty || visible.busy || visible.conflict || hasInvalidConsoleHelpDraft(visible.draft || [])}
          onClick={() => run(true)}>{visible.busy ? <CircularProgress size={18} aria-label={t("page_help_saving")} /> : t("save")}</Button>
        <Button type="button" variant="outlined" disabled={visible.busy || help?.loading} onClick={() => run(false)}>{t("page_help_reload")}</Button>
      </Stack>
      {(dirty || visible.conflict) && <Typography color="text.secondary" variant="body2">{t("page_help_reload_note")}</Typography>}
      {!visible.draft && help?.loading && <CircularProgress aria-label={t("page_help_loading")} />}
      {visible.draft?.map(row => {
        const section = t(CONSOLE_HELP_PAGES.find(page => page.pageKey === row.pageKey).labelKey);
        const emptyEnabled = row.enabled && !row.elUrl.trim() && !row.enUrl.trim();
        return <Box key={row.pageKey} component="section" aria-label={section}
          sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }}>
            <Typography variant="h6">{section}</Typography>
            <FormControlLabel label={t("page_help_enabled")} control={<Switch checked={row.enabled} disabled={visible.busy}
              onChange={event => update(row.pageKey, "enabled", event.target.checked)} inputProps={{ "aria-label": `${section}: ${t("page_help_enabled")}` }} />} />
          </Stack>
          {emptyEnabled && <Alert severity="warning" sx={{ mb: 2 }}>{t("page_help_url_required")}</Alert>}
          <Grid container spacing={2}>
            {[{ field: "elUrl", label: "page_help_greek_url" }, { field: "enUrl", label: "page_help_english_url" }].map(({ field, label }) => {
              const normalized = normalizeConsoleHelpUrl(row[field]);
              const invalid = Boolean(row[field].trim() && !normalized);
              return <Grid item xs={12} md={6} key={field}>
                <TextField fullWidth size="small" label={`${section}: ${t(label)}`} value={row[field]} disabled={visible.busy}
                  onChange={event => update(row.pageKey, field, event.target.value)} error={invalid}
                  helperText={invalid ? t("page_help_invalid_url") : t("page_help_url_hint")}
                  inputProps={{ autoComplete: "off", spellCheck: false }} />
                <Button type="button" size="small" startIcon={<OpenInNewOutlinedIcon />} disabled={!normalized || visible.busy}
                  {...(normalized ? { component: "a", href: normalized, target: "_blank", rel: "noopener noreferrer" } : {})}
                  aria-label={`${section}: ${t("page_help_preview")} ${t(label)}`}>{t("page_help_preview")}</Button>
              </Grid>;
            })}
          </Grid>
        </Box>;
      })}
    </Stack>
  </Box>;
}
